import { memo, useCallback, useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { openViewWindow } from '@renderer/overlay/Sidepanel/open-view-window'
import { trackNotchAction } from './track-notch'

export const MiniAskInputElementId = 'mini-ask-input'

const MINI_ASK_INPUT_MIN_HEIGHT = 24
const MINI_ASK_INPUT_MAX_HEIGHT = 112

interface MiniAskInputProps {
  /** Expanded while focused or holding a draft; reported on focus/blur/submit, never on mount. */
  onExpandedChange: (expanded: boolean) => void
}

export const MiniAskInput = memo(function MiniAskInput({ onExpandedChange }: MiniAskInputProps) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const hasInput = value.length > 0

  const handleFocus = useCallback(() => onExpandedChange(true), [onExpandedChange])
  const handleBlur = useCallback(() => onExpandedChange(hasInput), [hasInput, onExpandedChange])

  const resizeInput = useCallback(() => {
    const input = inputRef.current
    if (!input) {
      return
    }

    input.style.height = 'auto'
    const nextHeight = Math.min(Math.max(input.scrollHeight, MINI_ASK_INPUT_MIN_HEIGHT), MINI_ASK_INPUT_MAX_HEIGHT)
    input.style.height = `${nextHeight}px`
  }, [])

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input) {
      return
    }

    let previousWidth = input.clientWidth
    const observer = new ResizeObserver(() => {
      if (input.clientWidth === previousWidth) {
        return
      }
      previousWidth = input.clientWidth
      resizeInput()
    })
    observer.observe(input)
    return () => observer.disconnect()
  }, [resizeInput])

  useLayoutEffect(resizeInput, [resizeInput, value])

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value)
  }, [])

  const handleSubmit = useCallback(() => {
    const prompt = value.trim()
    if (!prompt) {
      return
    }

    trackNotchAction('ask submitted', { promptLength: prompt.length })
    setValue('')
    inputRef.current?.blur()
    onExpandedChange(false)

    // The popout sends the prompt itself, so it can open before the backend has assigned a chat id.
    openViewWindow({ id: 'chat', context: { isNew: true, draftId: uuidv4(), initialPrompt: prompt } })
  }, [onExpandedChange, value])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
        return
      }

      event.preventDefault()
      handleSubmit()
    },
    [handleSubmit],
  )

  return (
    <div className="hover:bg-element-hover focus-within:bg-element-hover corner-smooth flex w-full items-end gap-1 rounded-lg px-2 py-1 transition-colors">
      <textarea
        ref={inputRef}
        id={MiniAskInputElementId}
        rows={1}
        autoFocus
        aria-label="Ask Highlight"
        placeholder="Ask Highlight…"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // The caret and the box's focus fill mark focus, so it drops the global focus outline
        className="text-foreground placeholder:text-foreground-placeholder min-h-5 w-0 min-w-0 flex-1 select-text resize-none overflow-y-auto whitespace-pre-wrap break-words bg-transparent py-1 text-sm font-normal leading-5 outline-none focus-visible:outline-none"
      />
    </div>
  )
})
